import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { KpiTimeChart } from './components/KpiTimeChart';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { fmtNum, fmtNumShort, fmtMoney, fmtMoneyShort, fmtPeriodo } from './components/kpiUtils';
import { PiggyBank, Upload, Landmark, Coins, Wallet, CalendarDays } from 'lucide-react';

const fmtFecha = (iso) => { if (!iso) return '—'; const [y, m, d] = String(iso).slice(0, 10).split('-'); return `${d}/${m}/${y}`; };

const KpisCuentaRemunerada = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = async () => {
    try { const r = await api.get('/kpis/cuenta-remunerada'); setData(r.data); }
    catch (e) { toast.error('Error al cargar Cuenta Remunerada'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subir = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/cuenta-remunerada/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.total} días importados`); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const k = data?.kpis || {};
  const puede_cargar = data?.puede_cargar;
  const serie = (data?.serie || []).map((s) => ({ label: fmtFecha(s.fecha), cuentas: s.cuentas, saldo: s.saldo, remuneracion: s.remuneracion }));
  const porMes = (data?.por_mes || []).map((m) => ({ label: fmtPeriodo(m.periodo, true), remuneracion: m.remuneracion, saldo_promedio: m.saldo_promedio }));

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-cuenta-remunerada-loading">Cargando…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-cuenta-remunerada">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-teal-500/10"><PiggyBank className="h-6 w-6 text-teal-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Cuenta Remunerada</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-cuenta-remunerada" /></div>
            <p className="text-sm text-slate-500">Cuentas, saldos remunerados y costo de remuneración</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="cuenta_remunerada" testId="cuentarem-help-btn" />
          <SectionExportButton title="Cuenta Remunerada" targetRef={rootRef} testId="cuentarem-export-btn" />
          {puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-teal-600 hover:bg-teal-700 active:scale-95 transition-transform" data-testid="cuentarem-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir reporte (XLSX)'}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={subir} data-testid="cuentarem-file-input" />
            </>
          )}
        </div>
      </div>

      {!data?.cargado ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="cuentarem-empty">
          <PiggyBank className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Todavía no hay datos de cuenta remunerada cargados.</p>
          <p className="text-sm text-slate-400 mt-1">{puede_cargar ? 'Subí el Excel "Cuenta Remunerada".' : 'Pedile a un administrador que cargue el reporte.'}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <StatCard label="Cuentas remuneradas" value={fmtNum(k.cuentas)} icon={Wallet} accent="#0d9488" deltaLabel={k.fecha_ultimo ? `al ${fmtFecha(k.fecha_ultimo)}` : ''} testId="cuentarem-kpi-cuentas" />
            <StatCard label="Saldo remunerado" value={fmtMoney(k.saldo_total)} icon={Landmark} accent="#4f46e5" testId="cuentarem-kpi-saldo" />
            <StatCard label="Remuneración del día" value={fmtMoney(k.remuneracion_dia)} icon={Coins} accent="#f59e0b" testId="cuentarem-kpi-remun-dia" />
            <StatCard label="Remuneración acumulada" value={fmtMoney(k.remuneracion_acumulada)} icon={Coins} accent="#7c3aed" deltaLabel="período cargado" testId="cuentarem-kpi-remun-acum" />
            <StatCard label="Saldo prom. / cuenta" value={fmtMoney(k.saldo_promedio_cuenta)} icon={PiggyBank} accent="#0ea5e9" testId="cuentarem-kpi-saldo-prom" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="cuentarem-serie-saldo">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Saldo remunerado (diario)</h2>
              <KpiTimeChart data={serie} series={[{ key: 'saldo', name: 'Saldo', color: '#4f46e5' }]} type="area" valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} height={280} showLegend={false} testId="cuentarem-saldo-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="cuentarem-serie-cuentas">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Cuentas remuneradas (diario)</h2>
              <KpiTimeChart data={serie} series={[{ key: 'cuentas', name: 'Cuentas', color: '#0d9488' }]} type="area" valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={280} showLegend={false} testId="cuentarem-cuentas-chart" />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="cuentarem-serie-remun">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Costo de remuneración (diario)</h2>
              <KpiTimeChart data={serie} series={[{ key: 'remuneracion', name: 'Remuneración', color: '#f59e0b' }]} type="barras" valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} height={280} showLegend={false} testId="cuentarem-remun-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="cuentarem-por-mes">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Resumen mensual (remuneración)</h2>
              <KpiTimeChart data={porMes} series={[{ key: 'remuneracion', name: 'Remuneración', color: '#7c3aed' }]} type="barras" valueFormatter={fmtMoney} axisFormatter={fmtMoneyShort} height={280} showLegend={false} testId="cuentarem-mes-chart" />
            </Card>
          </div>

          <Card className="p-0 overflow-hidden" data-testid="cuentarem-tabla">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-slate-400" /> Detalle diario (últimos)</h2>
            </div>
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0"><tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                  <th className="text-left font-medium px-4 py-2.5">Fecha</th>
                  <th className="text-right font-medium px-4 py-2.5">Cuentas</th>
                  <th className="text-right font-medium px-4 py-2.5">Saldo remunerado</th>
                  <th className="text-right font-medium px-4 py-2.5">Remuneración</th>
                </tr></thead>
                <tbody>
                  {[...serie].reverse().map((s, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-2 whitespace-nowrap text-slate-600 dark:text-slate-300">{s.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtNum(s.cuentas)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(s.saldo)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtMoney(s.remuneracion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <p className="text-xs text-slate-400 mt-4">Fuente: {data?.archivo || 'Cuenta Remunerada'} · cargado {data?.fecha_carga ? new Date(data.fecha_carga).toLocaleString('es-AR') : '—'}</p>
        </>
      )}
    </div>
  );
};

export default KpisCuentaRemunerada;
